import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Store } from '@ngrx/store';

import { selectNavigatorStepStartTime } from '../../../../quickjs/quickjs.feature';
import { jobInventoryFeature } from '../../../job-inventory/job-inventory.feature';
import { InventoryRoom } from '../../../job-inventory/job-inventory.state';

import { NavBaseComponent } from './nav-base';

/**
 * Load (and Unload) progress tracker.
 *
 * Shows each room from the confirmed inventory with its total weight.
 * Crew taps "Mark Done" as each room finishes loading onto the truck.
 *
 * Dual progress display:
 *   - Time bar:   elapsed vs estimated (from estimatedSeconds prop)
 *   - Weight bar: lbs loaded vs total lbs (derived from done rooms)
 *
 * Gates the step confirm button via node.props.onReady — satisfied when every room is
 * marked done, which includes the zero-room case: nothing to load is nothing to block.
 *
 * Props:
 *   estimatedSeconds  — from calculator (used for time bar)
 *   phase             — 'load' | 'unload'
 *   crewSize          — number of crew members (for lbs/man-hr display)
 *   benchmarkRate     — default 750 lbs/man-hr
 */

interface LoadRoom {
    id: string;
    name: string;
    items: number;        // item count (confirmed, not missing)
    totalWeight: number;  // lbs
    done: boolean;
    doneAt?: number;      // unix ms
}

@Component({
    selector: 'app-nav-load-progress',
    standalone: true,
    imports: [CommonModule],
    styles: [`
        :host { display: block; margin-bottom: 1rem; }

        /* ── Dual progress card ── */
        .progress-card {
            background: #1a1a1a; color: white;
            border-radius: 10px; padding: 1rem 1.1rem; margin-bottom: 0.85rem;
        }
        .progress-row {
            display: flex; align-items: center; justify-content: space-between;
            margin-bottom: 0.5rem;
        }
        .progress-label { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.65; }
        .progress-value { font-size: 0.9rem; font-weight: 700; }
        .progress-track {
            height: 8px; background: rgba(255,255,255,0.15);
            border-radius: 4px; overflow: hidden; margin-bottom: 0.85rem;
        }
        .progress-track:last-child { margin-bottom: 0; }
        .progress-fill {
            height: 100%; border-radius: 4px; transition: width 0.5s ease;
        }
        .fill-time-good  { background: #22c55e; }
        .fill-time-warn  { background: #f59e0b; }
        .fill-time-over  { background: #ef4444; }
        .fill-weight     { background: #3b82f6; }

        /* ── Room list ── */
        .rooms-label {
            font-size: 0.75rem; font-weight: 700; text-transform: uppercase;
            letter-spacing: 0.06em; color: #888; margin-bottom: 0.5rem;
        }

        .room-card {
            background: white; border-radius: 10px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.07);
            margin-bottom: 0.6rem; overflow: hidden;
            transition: opacity 0.2s;
        }
        .room-card.done { opacity: 0.65; }

        .room-row {
            display: flex; align-items: center; gap: 0.85rem;
            padding: 0.85rem 1.1rem;
        }

        .room-icon {
            width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0;
            background: #f0f0f0; color: #888;
            display: flex; align-items: center; justify-content: center; font-size: 0.9rem;
        }
        .room-icon.done { background: #dcfce7; color: #16a34a; }

        .room-info { flex: 1; min-width: 0; }
        .room-name  { font-size: 0.95rem; font-weight: 600; color: #1a1a1a; }
        .room-meta  { font-size: 0.8rem; color: #888; margin-top: 0.1rem; }
        .room-meta.done { color: #16a34a; }

        .mark-done-btn {
            padding: 0.5rem 0.9rem; border-radius: 8px;
            font-size: 0.85rem; font-weight: 700; cursor: pointer;
            font-family: inherit; white-space: nowrap; border: none;
            background: #2d6a2d; color: white;
        }
        .mark-done-btn:active { background: #1f4d1f; }

        .done-badge {
            display: flex; align-items: center; gap: 0.3rem;
            font-size: 0.82rem; font-weight: 700; color: #16a34a;
        }

        /* ── All done banner ── */
        .all-done-banner {
            background: #f0f7f0; border-radius: 10px;
            padding: 1rem 1.1rem;
            display: flex; align-items: center; gap: 0.75rem;
            color: #2d6a2d; font-weight: 700; font-size: 0.95rem;
        }
        .all-done-banner i { font-size: 1.3rem; }

        /* ── Empty state ── */
        .no-rooms {
            background: white; border-radius: 10px;
            padding: 1.5rem; text-align: center; color: #aaa; font-size: 0.9rem;
            box-shadow: 0 1px 4px rgba(0,0,0,0.07);
        }
        .no-rooms-sub { margin-top: 0.4rem; font-size: 0.82rem; color: #bbb; }
    `],
    template: `
        <!-- Dual progress: time + weight -->
        <div class="progress-card">
            <!-- Time -->
            <div class="progress-row">
                <span class="progress-label">Time</span>
                <span class="progress-value">{{ elapsedDisplay }} / {{ estimatedDisplay }}</span>
            </div>
            <div class="progress-track">
                <div class="progress-fill"
                    [class.fill-time-good]="timeStatus === 'good'"
                    [class.fill-time-warn]="timeStatus === 'warn'"
                    [class.fill-time-over]="timeStatus === 'over'"
                    [style.width.%]="timePct">
                </div>
            </div>

            <!-- Weight -->
            <div class="progress-row">
                <span class="progress-label">Weight {{ phaseVerb }}</span>
                <span class="progress-value">
                    {{ weightDone | number:'1.0-0' }} / {{ totalWeight | number:'1.0-0' }} lbs
                    <span *ngIf="lbsPerManHr > 0" style="opacity:0.65;font-weight:500;font-size:0.78rem;margin-left:0.4rem;">
                        · {{ lbsPerManHr | number:'1.0-0' }} lbs/man-hr
                    </span>
                </span>
            </div>
            <div class="progress-track">
                <div class="progress-fill fill-weight" [style.width.%]="weightPct"></div>
            </div>
        </div>

        <!-- Room list -->
        <div class="rooms-label" *ngIf="rooms.length > 0">Rooms — tap Mark Done as each one is {{ phaseVerb }}</div>

        <!--
            Zero rooms is a SATISFIED gate, not a blocked one (see allDone), so this must not send
            the crew back. Reached both by a job with no inventory and by one where Confirm Scope
            was cleared with a single empty room — the items > 0 filter below drops it, so the two
            are indistinguishable here.
        -->
        <div *ngIf="rooms.length === 0" class="no-rooms">
            <i class="pi pi-info-circle" style="font-size:1.2rem;display:block;margin-bottom:0.4rem;"></i>
            No itemised inventory for this job — nothing to check off here.
            <div class="no-rooms-sub">You can continue to the next step.</div>
        </div>

        <div class="room-card" *ngFor="let r of rooms; trackBy: trackById" [class.done]="r.done">
            <div class="room-row">
                <div class="room-icon" [class.done]="r.done">
                    <i [class]="r.done ? 'pi pi-check' : 'pi pi-home'"></i>
                </div>
                <div class="room-info">
                    <div class="room-name">{{ r.name }}</div>
                    <div class="room-meta" [class.done]="r.done">
                        <ng-container *ngIf="!r.done">
                            {{ r.items }} item{{ r.items !== 1 ? 's' : '' }}
                            <ng-container *ngIf="r.totalWeight > 0"> · {{ r.totalWeight | number:'1.0-0' }} lbs</ng-container>
                        </ng-container>
                        <ng-container *ngIf="r.done">
                            ✓ {{ phaseVerb | titlecase }} · {{ r.totalWeight | number:'1.0-0' }} lbs
                        </ng-container>
                    </div>
                </div>
                <button class="mark-done-btn" *ngIf="!r.done" (click)="markDone(r)">
                    Mark Done
                </button>
                <span class="done-badge" *ngIf="r.done">
                    <i class="pi pi-check-circle"></i> Done
                </span>
            </div>
        </div>

        <!-- All rooms done -->
        <div class="all-done-banner" *ngIf="allDone && rooms.length > 0">
            <i class="pi pi-check-circle"></i>
            All rooms {{ phaseVerb }} — {{ totalWeight | number:'1.0-0' }} lbs in {{ elapsedDisplay }}
        </div>
    `,
})
export class NavLoadProgressComponent extends NavBaseComponent implements OnInit, OnDestroy {

    rooms: LoadRoom[] = [];
    private _startMs = Date.now();
    private _tick: any;

    // Timer display
    elapsedSec = 0;
    elapsedDisplay = '00:00';
    estimatedDisplay = '';

    constructor(private store: Store, private cdr: ChangeDetectorRef) { super(); }

    // ── Computed ──────────────────────────────────────────────────────────────

    get estimatedSeconds(): number { return this.node.props?.estimatedSeconds ?? 0; }
    get crewSize(): number { return this.node.props?.crewSize ?? 2; }
    get benchmarkRate(): number { return this.node.props?.benchmarkRate ?? 750; }
    get phase(): string { return this.node.props?.phase ?? 'load'; }

    /** Load and Unload are the same tag with different props, so the wording lives here. */
    get phaseVerb(): string { return this.phase === 'unload' ? 'unloaded' : 'loaded'; }

    get totalWeight(): number { return this.rooms.reduce((s, r) => s + r.totalWeight, 0); }
    get weightDone(): number  { return this.rooms.filter(r => r.done).reduce((s, r) => s + r.totalWeight, 0); }
    get weightPct(): number   { return this.totalWeight > 0 ? Math.min(100, (this.weightDone / this.totalWeight) * 100) : 0; }

    get timePct(): number {
        if (!this.estimatedSeconds) return 0;
        return Math.min(110, (this.elapsedSec / this.estimatedSeconds) * 100);
    }
    get timeStatus(): 'good' | 'warn' | 'over' {
        const ratio = this.estimatedSeconds ? this.elapsedSec / this.estimatedSeconds : 0;
        return ratio < 0.85 ? 'good' : ratio < 1.05 ? 'warn' : 'over';
    }

    get lbsPerManHr(): number {
        const hrs = this.elapsedSec / 3600;
        if (!hrs || !this.crewSize || !this.weightDone) return 0;
        return Math.round(this.weightDone / (hrs * this.crewSize));
    }

    /**
     * No rooms means nothing to load, which is a satisfied gate — not a blocked one.
     * The `rooms.length > 0 &&` guard this replaces made a job with no inventory
     * impossible to advance past, because there was also no room row to tap.
     */
    get allDone(): boolean {
        return this.rooms.every(r => r.done);
    }

    trackById(_: number, r: LoadRoom) { return r.id; }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    ngOnInit() {
        // Estimated display
        this.estimatedDisplay = this._fmt(this.estimatedSeconds);

        // Use the step start time from the store — this is when the crew confirmed
        // the PREVIOUS step and this step began, not when the component mounted.
        // Falls back to Date.now() only if not yet set (shouldn't happen in practice).
        // Deliberate one-shot read of the current value, not an ongoing subscription.
        // eslint-disable-next-line @ngrx/no-store-subscription
        this.store.select(selectNavigatorStepStartTime).subscribe(t => {
            if (t) this._startMs = t;
        }).unsubscribe();

        // Load rooms from inventory store — one-shot snapshot taken when the step mounts.
        // eslint-disable-next-line @ngrx/no-store-subscription
        this.store.select(jobInventoryFeature.selectInventoryEditor).subscribe(inv => {
            if (!inv?.rooms?.length) return;
            // Only rooms with confirmed items (exclude fully-missing rooms)
            this.rooms = inv.rooms
                .map((r: InventoryRoom) => {
                    const confirmedItems = (r.inventory ?? []).filter((it: any) => {
                        // Items without explicit status (raw inventory) count as present
                        return it.quantity > 0;
                    });
                    const weight = confirmedItems.reduce((s: number, it: any) =>
                        s + (it.totalWeight ?? (it.weight ?? 0) * (it.quantity ?? 1)), 0);
                    return {
                        id: r.id,
                        name: r.name,
                        items: confirmedItems.length,
                        totalWeight: Math.round(weight),
                        done: false,
                    };
                })
                .filter((r: LoadRoom) => r.items > 0);
        }).unsubscribe();

        // Report the starting state to the step gate. Without this the gate stays at its
        // registered `satisfied: false` until a room is tapped — and with no inventory there
        // is no room to tap, so the step could never be confirmed.
        this._notify();

        // Start timer
        const tick = () => {
            this.elapsedSec = Math.floor((Date.now() - this._startMs) / 1000);
            this.elapsedDisplay = this._fmt(this.elapsedSec);
            this.cdr.markForCheck();
        };
        tick();
        this._tick = setInterval(tick, 1000);

        // Register initial gate state
        this._notify();
    }

    ngOnDestroy() { clearInterval(this._tick); }

    // ── Actions ───────────────────────────────────────────────────────────────

    markDone(room: LoadRoom) {
        room.done = true;
        room.doneAt = Date.now();
        this._notify();
    }

    private _notify() {
        if (typeof this.node.props?.onReady === 'function') {
            this.node.props.onReady(this.allDone);
        }
    }

    private _fmt(s: number): string {
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
        const sec = (s % 60).toString().padStart(2, '0');
        return h > 0 ? `${h}:${m}:${sec}` : `${m}:${sec}`;
    }
}
